import { IAuthData } from "../../../interfaces/store/authStore";
import { useChromeRuntime } from "../useChromeRuntime"

export type TGetConversationsPageOpts = {
    filter?: { archived: string; read: string }
    query?: string
    /** GraphQL voyager params (queryId, searchQueryId, timestamp, profileId, profileWithConversationGraphQL, conversationInfoGraphQL) */
    voyagerMessagingGraphQL?: TVoyagerMessagingGraphQLParams
    /** GraphQL pagination cursor (od INBOX_GET_CONVERSATIONS_V1 odgovora) */
    nextCursor?: string
}

export type TVoyagerMessagingGraphQLParams = {
    queryId: string
    searchQueryId: string
    profileId: string
    timestamp: number
    profileWithConversationGraphQL?: string
    conversationInfoGraphQL?: string
}

/**
 * Shapes the logged-auth payload before the extension persists it (e.g. chrome.storage.sync, which has
 * tight quotas). Drop anything that is app-only, bulky, or unnecessary for the extension — add more
 * keys here as needed.
 */
const sanitizeExtensionAuthPayload = (payload: any) => {
    const w = payload?.user?.workspace
    if (!w || typeof w !== 'object') return payload
    const workspaceRest = { ...w }
    delete workspaceRest.customFields
    return {
        ...payload,
        user: {
            ...payload.user,
            workspace: workspaceRest,
        },
    }
}

/**
 * Inbox BG replies are sometimes `{ result: T }`, sometimes `{ conversations, nextCursor }` or raw array.
 * `useChromeRuntime` only unwraps `.result`; without this, the app sees `undefined`.
 *
 * IMPORTANT: If we return only `raw.conversations` while `nextCursor` lives on the same object, the app
 * loses the LI pagination cursor — `allConversationsFetched` flips true (REST hasNext false) and scroll
 * never loads more.
 */
function unwrapInboxExtensionPayload(raw: any): any {
    if (raw instanceof Error || raw === undefined || raw === false) return raw
    if (Array.isArray(raw)) return raw
    if (raw && typeof raw === 'object' && raw.result !== undefined) return raw.result
    if (raw?.conversations !== undefined) {
        if (raw && typeof raw === 'object' && 'nextCursor' in raw) return raw
        return raw.conversations
    }
    if (raw?.data?.conversations !== undefined) {
        const d = raw.data
        if (d && typeof d === 'object' && 'nextCursor' in d) return d
        return d.conversations
    }
    if (Array.isArray(raw?.elements)) return raw.elements
    return raw
}

export const useExtensionRequest = () => {
    const { sendMessage } = useChromeRuntime();
    
    // Extension status - GET
    const getExtensionStatus = () => {
        return sendMessage({
            slug: 'extensionStatus',
            action: 'action',
            url: 'status'
        })
    }

    //User related calls - GET
    const getLoggedInLIUser = () => {
        return sendMessage({
            slug: 'linkedInUser',
            action: 'linkedinAction',
            url: 'getLoggedInUser'
        })
    }

    //Get user data saved in chrome storage - GET
    const getLDAuthData = () => {
        return sendMessage({
            slug: 'getLDAuthData',
            action: 'action',
            url: 'getLDAuthData'
        })
    }

    //Get tokens from extenstion - GET
    const getSessionTokens = () => {
        return sendMessage({
            slug: 'getSessionTokens',
            action: 'action',
            url: 'getAppTokens'
        });
    }

    //all set to true since error handler is outside
    const getProfileContactInfo = (payload: { profileId: string }) => {
        return sendMessage({
            slug: 'getProfileContactInfo',
            action: 'linkedinAction',
            url: 'getProfileContactInfo',
            payload,
            all: true
        });
    }

    const getProfileView = (payload: { profileId: string }) => {
        return sendMessage({
            slug: 'getProfileView',
            action: 'linkedinAction',
            url: 'getProfileView',
            payload,
            all: true
        });
    }

    //This profileId is public identifier, not a real profile id
    const getProfileExtraData = (payload: { profileId: string }) => {
        return sendMessage({
            slug: 'getExtraProfileData',
            action: 'linkedinAction',
            url: 'getExtraProfileData',
            payload,
            all: true
        });
    }

    const getProfilePosts = (profileUrn: string) => {
        const profileCardsQueryId = process.env.REACT_APP_PROFILE_CARDS_QUERY_ID
        const profileCardsGetAllQueryId = process.env.REACT_APP_PROFILE_CARDS_GET_ALL_QUERY_ID
        const profileCardsGetAllStart = process.env.REACT_APP_PROFILE_CARDS_GET_ALL_START
        const profileCardsGetAllCount = process.env.REACT_APP_PROFILE_CARDS_GET_ALL_COUNT
        return sendMessage({
            slug: 'getProfilePosts',
            action: 'linkedinAction',
            url: 'getProfilePosts',
            payload: {
                params: { profileUrn, profileCardsQueryId, profileCardsGetAllQueryId, profileCardsGetAllStart, profileCardsGetAllCount }
            }
        });
    }

    const getJobChanges = () => {
        return sendMessage({
            slug: 'getJobChanges',
            action: 'linkedinAction',
            url: 'getJobChanges'
        });
    }

    const likeProfilePosts = (payload: any, options?: { all?: boolean }) => {
        return sendMessage({
            slug: 'likeProfilePosts',
            action: 'linkedinAction',
            url: 'likeProfilePosts',
            payload: {
                params: payload
            },
            all: options?.all ?? false,
            displayToast: false
        });
    }

    const commentProfilePosts = (payload: any, options?: { all?: boolean }) => {
        return sendMessage({
            slug: 'commentProfilePost',
            action: 'linkedinAction',
            url: 'commentProfilePost',
            payload: {
                params: payload
            },
            all: options?.all ?? false,
            displayToast: false
        });
    }

    const editProfilePostComment = (payload: any) => {
        return sendMessage({
            slug: 'editProfilePostComment',
            action: 'linkedinAction',
            url: 'editProfilePostComment',
            payload: {
                params: payload
            },
            displayToast: false
        });
    }

    const deleteProfilePostComment = (commentUrn: string) => {
        return sendMessage({
            slug: 'deleteProfilePostComment',
            action: 'linkedinAction',
            url: 'deleteProfilePostComment',
            payload: {
                params: {
                    commentUrn: commentUrn
                }
            },
            displayToast: false
        });
    }

    /** Fetch one page of LinkedIn post comments (Social Dash Comments GraphQL). */
    const getProfilePostComments = (params: {
        ugcPostId?: string;
        socialDetailUrn?: string;
        count?: number;
        start?: number;
        numReplies?: number;
        sortOrder?: string;
        paginationToken?: string;
        queryId?: string;
    }) => {
        return sendMessage({
            slug: 'getProfilePostComments',
            action: 'linkedinAction',
            url: 'getProfilePostComments',
            payload: { params },
            displayToast: false,
        });
    }

    const setGraphqlQueryIds = () => {
        return sendMessage({
            slug: 'setGraphqlQueryIds',
            action: 'linkedinAction',
            url: 'setGraphqlQueryIds',
            payload: {
                graphqlQueryIds: {
                    getLIUserHandlerQueryId: process.env.REACT_APP_GET_LI_USER_HANDLER_QUERY_ID,
                    contactInfoQueryId: process.env.REACT_APP_CONTACT_INFO_QUERY_ID,
                    meNetworkInfoQueryId: process.env.REACT_APP_ME_NETWORK_INFO_QUERY_ID,
                    jobHistoryQueryId: process.env.REACT_APP_JOB_HISTORY_QUERY_ID,
                    socialDashCommentsQueryId: process.env.REACT_APP_SOCIAL_DASH_COMMENTS_QUERY_ID
                }
            },
            displayToast: false
        });
    }

    const syncConnections = (forceUpdate?: boolean) => {
        return sendMessage({
            slug: 'syncConnections',
            action: 'linkedinAction',
            url: 'getFormattedOwnContacts',
            payload:  { forceUpdate: forceUpdate ? forceUpdate : undefined }
        })
    }

    /**
     * V1 GraphQL inbox poziv — koristi LinkedIn voyagerMessagingGraphQL endpoint
     * (`messengerConversations.<queryId>`), ne legacy `messaging/conversations`.
     * Vraća **samo niz razgovora** (uz to ekstenzija već uradi `synchronizeConversations`
     * na backend prilikom poslednje stranice / ukoliko ima razgovora).
     */
    const syncConversations = (opts?: TGetConversationsPageOpts) => {
        if (!opts?.voyagerMessagingGraphQL) {
            console.warn('[Inbox ext] syncConversations: nedostaje voyagerMessagingGraphQL — pozovi getInboxQueryParams() i prosledi rezultat.')
            return Promise.resolve(false as any)
        }

        const archived = opts?.filter?.archived ?? 'No'
        const read = opts?.filter?.read ?? 'All'
        const query = opts?.query ?? ''

        const params = {
            pinned: false,
            voyagerMessagingGraphQL: {
                ...opts.voyagerMessagingGraphQL,
                archived,
                read,
                query,
                nextCursor: opts.nextCursor ?? '',
            },
        }
        console.log('[Inbox ext] → BG inboxGetConversationsV1 (firstPage)', { params })

        return sendMessage({
            slug: 'inboxGetConversationsV1',
            action: 'action',
            url: 'inboxGetConversationsV1',
            payload: { params },
            all: true,
        }).then((raw: any) => {
            console.log('[Inbox ext] inboxGetConversationsV1 raw response', raw)
            if (raw instanceof Error) return false
            const payload = unwrapInboxExtensionPayload(raw)
            console.log('[Inbox ext] inboxGetConversationsV1 unwrapped', payload)
            return payload
        })
    }

    /**
     * Sales Navigator — same call as in extension (`inboxGetConversations` + `salesNavigator: true`).
     * Pagination: `createdBefore` (number) = previous response `nextPageStartsAt` from the last conversation.
     * Returns `{ conversations, nextCursor }` so the rest of the app uses `extractNextCursor`.
     */
    const syncSalesNavigatorConversations = (opts?: {
        nextCursor?: string
        query?: string
        filter?: { archived: string; read: string }
    }) => {
        const params: Record<string, unknown> = {
            salesNavigator: true,
            filter: opts?.filter ?? { archived: 'All', read: 'All' },
        }
        const trimmedQuery = opts?.query?.trim()
        if (trimmedQuery) {
            params.query = trimmedQuery
        }
        const token = opts?.nextCursor
        if (token != null && token !== '') {
            const n = Number(token)
            if (!Number.isNaN(n)) params.createdBefore = n
        }

        return sendMessage({
            slug: 'inboxGetConversationsSn',
            action: 'action',
            url: 'inboxGetConversations',
            payload: { params },
            all: true,
        }).then((raw: any) => {
            if (raw instanceof Error) return false
            const unwrapped = unwrapInboxExtensionPayload(raw)
            const conversations = Array.isArray(unwrapped)
                ? unwrapped
                : Array.isArray((unwrapped as any)?.conversations)
                  ? (unwrapped as any).conversations
                  : []
            const last = conversations[conversations.length - 1] as { nextPageStartsAt?: number | string } | undefined
            const firstWithPointer = conversations.find((c: any) => c?.nextPageStartsAt != null && c?.nextPageStartsAt !== '')
            const startsAt = last?.nextPageStartsAt ?? firstWithPointer?.nextPageStartsAt
            const nextCursor =
                startsAt != null && startsAt !== ''
                    ? String(startsAt)
                    : null
            return { conversations, nextCursor }
        })
    }

    /**
     * Pagination preko V1 GraphQL — LI vraća `nextCursor`; prosledi ga sledećem pozivu.
     * Drugi argument je za backward-compat sa starom `createdBefore` signaturom (ignoriše se).
     * Vraća payload `{ conversations, nextCursor }` ili niz (zavisi od BG verzije).
     */
    const getConversationsPage = (_createdBefore?: number, opts?: TGetConversationsPageOpts) => {
        if (!opts?.voyagerMessagingGraphQL) {
            console.warn('[Inbox ext] getConversationsPage: nedostaje voyagerMessagingGraphQL — pozovi getInboxQueryParams() i prosledi rezultat.')
            return Promise.resolve(false as any)
        }

        const archived = opts?.filter?.archived ?? 'No'
        const read = opts?.filter?.read ?? 'All'
        const query = opts?.query ?? ''

        const params = {
            pinned: false,
            voyagerMessagingGraphQL: {
                ...opts.voyagerMessagingGraphQL,
                archived,
                read,
                query,
                nextCursor: opts.nextCursor ?? '',
            },
        }
        if (process.env.NODE_ENV === 'development') {
            console.log('[Inbox ext] → BG inboxGetConversationsV1 (page)', { nextCursor: opts.nextCursor, params })
        }

        return sendMessage({
            slug: 'inboxGetConversationsV1',
            action: 'action',
            url: 'inboxGetConversationsV1',
            payload: { params },
            all: true,
        }).then((raw: any) => {
            if (process.env.NODE_ENV === 'development') {
                console.log('[Inbox ext] inboxGetConversationsV1(page) raw', raw)
            }
            if (raw instanceof Error) return false
            const payload = unwrapInboxExtensionPayload(raw)
            if (process.env.NODE_ENV === 'development') {
                console.log('[Inbox ext] inboxGetConversationsV1(page) unwrapped', payload)
            }
            return payload
        })
    }

    const iterateHubSpotQueue = (queueItems?: any[]) => {
        return sendMessage({
            slug: 'inboxIterateQueue',
            action: 'action',
            url: 'inboxIterateQueue',
            payload: {
                params: {
                    queueItems: queueItems || undefined
                }
            },
            displayToast: false
        })
    }

    //Push tokens to extenstion - PUT
    const putSessionTokens = (payload: IAuthData) => {
        return sendMessage({
            slug: 'putSessionTokens',
            action: 'action',
            url: 'putAppTokens',
            payload
        })
    }

    const openExtensionInTab = (payload: any) => {
        return sendMessage({
            slug: 'openExtensionInTab',
            action: 'action',
            url: 'openExtensionInTab',
            payload
        })
    }

    const setUserLoggedAuth = (payload: any) => {
        return sendMessage({
            slug: 'setUserLoggedAuth',
            action: 'action',
            url: 'setUserLoggedAuth',
            payload: sanitizeExtensionAuthPayload(payload)
        })
    }

    //Cheap trick so the store is initialized on the onboarding
    const getLDAccessToken = () => {
        return sendMessage({
            slug: 'getLDAccessToken',
            action: 'action',
            url: 'getLDAccessToken'
        })
    }
    //This is deprecated, use createOrSendMessage instead
    const messageProfile = (profileId: string, messageText: string) => {
        return sendMessage({
            slug: 'messageProfile',
            action: 'linkedinAction',
            url: 'messageProfile',
            payload: {
                profileId,
                messageText
            }
        })
    }

    const followProfile = (profileId: string) => {
        return sendMessage({
            slug: 'follow',
            action: 'linkedinAction',
            url: 'follow',
            payload: {
                profileId
            }
        })
    }

    const unfollowProfile = (profileId: string) => {
        return sendMessage({
            slug: 'unfollow',
            action: 'linkedinAction',
            url: 'unfollow',
            payload: {
                profileId
            }
        })
    }

    const disconnectProfile = (profileId: string) => {
        return sendMessage({
            slug: 'disconnect',
            action: 'linkedinAction',
            url: 'disconnect',
            payload: {
                profileId
            }
        })
    }

    const sendConnectionRequest = (target: string, message: string = '') => {
        const trimmed = target.trim();
        const isDirectUrn = (trimmed.startsWith('urn:li:') || /^ACoA[A-Za-z0-9_-]+$/.test(trimmed))
            && !trimmed.includes('linkedin.com');

        return sendMessage({
            slug: 'sendConnectionRequest',
            action: 'action',
            url: 'send_connection_request',
            payload: isDirectUrn
                ? { params: { memberUrn: trimmed }, note: message }
                : { publicIdentifier: trimmed, note: message },
            all: true,
            displayToast: false,
        });
    }

    const setupTemplatesContextMenu = (authData: any) => {
        return sendMessage({
            slug: 'setupTemplatesContextMenu',
            action: 'action',
            url: 'setupTemplatesContextMenu',
            payload: {
                loginData: {
                    token: authData?.accessToken?.split(' ')[1],
                    refreshToken: authData?.refreshToken
                }
            }
        })
    }

    const storeTabId = (tab: any) => {
        return sendMessage({
            slug: 'backToApp',
            action: 'action',
            url: 'backToApp',
            payload: {
                props: tab
            }
        })
    }

    const getMessagesFromConversation = (conversationId: any, createdBefore?: any, isSales?: boolean) => {
        return sendMessage({
            slug: 'getMessagesFromConversation',
            action: 'linkedinAction',
            url: 'getMessagesFromConversation',
            payload: {
                params: {
                    conversationId,
                    createdBefore,
                    ...(isSales ? { isSales: true } : {}),
                }
            }
        })
    }

    const sendMessageInConversation = (conversationId: any, text?: any, isSales?: boolean) => {
        return sendMessage({
            slug: 'sendMessageInConversation',
            action: 'linkedinAction',
            url: 'sendMessageInConversation',
            payload: {
                params: {
                    conversationId,
                    ...(isSales ? { isSales: true } : {}),
                },
                body: { messageContent: { text } }
            }
        })
    }

    const createOrSendMessage = (payload: any, options?: { all?: boolean }) => {
        const { profileId, participantUrns, conversationId, text, attachments, isSales } = payload;
        const participantUrnsArray = (Array.isArray(participantUrns) ? participantUrns : [participantUrns])
            .filter((urn: any) => urn != null);

        const participantUrnsWithPrefix = participantUrnsArray.map((participantUrn: string) =>
            participantUrn.startsWith('urn:li:') ? participantUrn : `urn:li:fsd_profile:${participantUrn}`
        );

        return sendMessage({
            slug: 'createOrSendMessage',
            action: 'linkedinAction',
            url: 'createOrSendMessage',
            payload: {
                conversationId: conversationId,
                profileId: profileId,
                participantUrns: participantUrnsWithPrefix,
                // Send both formats: messageText (flat) is what the extension validates against,
                // messageContent is kept for backward compatibility with older extension versions.
                messageText: text,
                messageContent: {
                    text,
                    ...(attachments ? { attachments } : {}),
                },
                ...(isSales ? { isSales: true } : {}),
            },
            all: options?.all ?? false,
            displayToast: false,
        })
    }

    const getConversationsFromParticipants = (participantUrn: string, profileId: string) => {
        return sendMessage({
            slug: 'getConversationsFromParticipants',
            action: 'linkedinAction',
            url: 'getConversationsFromParticipants',
            payload: {
                params: { 
                    vanityName: participantUrn,
                    profileId: profileId,
                    queryId: 'a1a483e719b20537a256b6853cdca711'
                }
            }
        })
    }

    // current job title and array of previous jobs
    const getJobHistoryData = (payload: { profileId: string }) => {
        return sendMessage({
            slug: 'getJobHistoryData',
            action: 'linkedinAction',
            url: 'getJobHistoryData',
            payload,
            all: true
        });
    }

    const searchPeopleSdui = (payload: { connectionOf: string, cacheId?: string, nextPageBindingKey?: string | null, pageNumber?: number }) => {
        return sendMessage({
            slug: 'searchPeopleSdui',
            action: 'linkedinAction',
            url: 'searchPeopleSdui',
            payload: {
                params: payload,
            },
            all: true,
        });
    };

    const mutualConnectionsFromLinkedinTab = (payload: { connectionOf: string; start?: number; voyagerSearchDashClustersQueryId: string; }) => {
        return sendMessage({
            slug: 'mutualConnectionsFromLinkedinTab',
            action: 'linkedinAction',
            url: 'mutualConnectionsFromLinkedinTab',
            payload: {
                params: payload,
            },
            all: true,
        });
    };

    const getProfileByIdentityDash = (payload: { vanityName: string, identityDashProfileByVanityQueryId: string }) => { //vanityName is public identifier
        return sendMessage({
            slug: 'getProfileByIdentityDash',
            action: 'linkedinAction',
            url: 'getProfileByIdentityDash',
            payload: {
                params: payload,
            },
            all: true
        });
    }

    const inboxMarkAsSeen = (conversationId: string, read: boolean) => {
        return sendMessage({
            slug: 'inboxMarkAsSeen',
            action: 'action',
            url: 'inboxMarkAsSeen',
            payload: {
                params: { conversationId },
                body: { read }
            },
            displayToast: false
        })
    }

    const inboxSyncLinkedinConversationStar = (conversationIds: string[], isStarred: boolean, viewerProfileId: string) => {
        return sendMessage({
            slug: 'inboxSyncLinkedinConversationStar',
            action: 'action',
            url: 'inboxSyncLinkedinConversationStar',
            payload: {
                params: {
                    conversationIds,
                    isStarred,
                    viewerProfileId,
                },
            },
            displayToast: false,
        })
    }

    const inboxArchiveConversation = (conversationId: string, archived: boolean) => {
        return sendMessage({
            slug: 'inboxArchiveConversation',
            action: 'action',
            url: 'inboxArchiveConversation',
            payload: {
                params: { conversationId },
                body: { archived }
            },
            displayToast: false
        })
    }

    const inboxArchiveConversations = (conversationIds: string[], archived: boolean) => {
        return sendMessage({
            slug: 'inboxArchiveConversations',
            action: 'action',
            url: 'inboxArchiveConversations',
            payload: {
                params: { conversationIds },
                body: { archived }
            },
            displayToast: false
        })
    }

    const inboxDeleteConversation = (conversationId: string) => {
        return sendMessage({
            slug: 'inboxDeleteConversation',
            action: 'action',
            url: 'inboxDeleteConversation',
            payload: {
                params: { conversationId }
            },
            displayToast: false
        })
    }

    const visitLinkedInConversation = (conversationId: string) => {
        return sendMessage({
            slug: 'visitLinkedInConversation',
            action: 'linkedinAction',
            url: 'visitConversation',
            payload: { conversationId },
            displayToast: false
        })
    }

    const visitLinkedInProfile = (profileId: string) => {
        return sendMessage({
            slug: 'visitLinkedInProfile',
            action: 'linkedinAction',
            url: 'visitProfile',
            payload: { profileId },
            displayToast: false
        })
    }

    const extractLinkedinProfileHtml = (params: {
        pageUrl: string
        includeHtml?: boolean
        profileId?: string
    }) => {
        const { pageUrl, includeHtml = false, profileId } = params
        return sendMessage({
            slug: 'extractLinkedinProfileHtml',
            action: 'linkedinAction',
            url: 'extractLinkedinProfileHtml',
            payload: {
                url: pageUrl,
                includeHtml,
                ...(profileId ? { profileId } : {}),
            },
            displayToast: false,
        })
    }

    /** Fetch post page HTML via extension and extract activity/ugcPost URNs (same idea as profile HTML). */
    const resolveLinkedinPostUrns = (params: {
        url: string
        preferredId?: string
    }) => {
        const { url, preferredId } = params
        return sendMessage({
            slug: 'resolveLinkedinPostUrns',
            action: 'linkedinAction',
            url: 'resolveLinkedinPostUrns',
            payload: {
                url,
                ...(preferredId ? { preferredId } : {}),
            },
            displayToast: false,
        })
    }

    /** Sales Navigator lead lists owned by the viewer (`salesApiLists`). */
    const getSalesLists = () => {
        return sendMessage({
            slug: 'getSalesLists',
            action: 'linkedinAction',
            url: 'getSalesLists',
            displayToast: false,
        })
    }

    /**
     * Full Sales Navigator profile (`salesApiProfiles`) — needs authType/authToken
     * from `urn:li:fs_salesProfile:(id,authType,authToken)`. Returns flagshipProfileUrl / vanity.
     */
    const getSalesNavigatorProfile = (params: {
        SNprofileId: string
        authType: string
        authToken: string
    }) => {
        return sendMessage({
            slug: 'getSalesNavigatorProfile',
            action: 'linkedinAction',
            url: 'getSalesNavigatorProfile',
            payload: { params },
            displayToast: false,
        })
    }

    /**
     * One page of Sales Navigator lead search (`salesApiLeadSearch`).
     * Pass `rawQuery` (e.g. LEAD_LIST filter) or `queryUrl` / `recentSearchId`.
     * Pagination: start=0,25,50… count≥25.
     */
    const searchLeadsSN = (query: {
        start?: number;
        count?: number;
        rawQuery?: string;
        queryUrl?: string;
        recentSearchId?: string;
        sessionId?: string;
    }) => {
        return sendMessage({
            slug: 'searchLeadsSN',
            action: 'linkedinAction',
            url: 'searchLeadsSN',
            payload: { query },
            displayToast: false,
        })
    }

    const ldAddNewConnection = (profile: Record<string, unknown>) => {
        return sendMessage({
            slug: 'LDAddNewConnection',
            action: 'linkedinAction',
            url: 'LDAddNewConnection',
            payload: { body: profile },
            displayToast: false,
        })
    }

    const getLoggedInLIUserForInbox = () => {
        return sendMessage({
            slug: 'linkedInUserInbox',
            action: 'linkedinAction',
            url: 'getLoggedInUser',
            displayToast: false
        })
    }

    const startHubSpotQueueInterval = (params: { workspaceId: string, intervalMinutes?: number }) => {
        return sendMessage({
            slug: 'startHubSpotQueueInterval',
            action: 'action',
            url: 'inboxStartHubspotQueueInterval',
            payload: { params },
            all: true
        });
    }

    const checkHubSpotQueueLastRun = (params: { workspaceId: string }) => {
        return sendMessage({
            slug: 'checkHubSpotQueueLastRun',
            action: 'action',
            url: 'inboxCheckHubspotQueueLastRun',
            payload: { params },
            all: true
        });
    }

    const queueInboxHubspotExport = (items: any[], isSales = false) => {
        return sendMessage({
            slug: 'inboxQueueToHubspot',
            action: 'action',
            url: 'inboxQueueToHubspot',
            payload: { params: { items, isSales } },
            all: true,
            displayToast: false,
        })
    }

    const getHubspotInboxesFromExtension = () => {
        return sendMessage({
            slug: 'inboxGetHubspotInboxes',
            action: 'action',
            url: 'inboxGetHubspotInboxes',
            all: true,
            displayToast: false,
        })
    }

    const getHubspotOAuthStatusFromExtension = () => {
        return sendMessage({
            slug: 'inboxGetHubspotOAuthStatus',
            action: 'action',
            url: 'inboxGetHubspotOAuthStatus',
            all: true,
            displayToast: false,
        })
    }

    /** Same handshake App.tsx runs on load — required before extension can read LinkedIn. */
    const registerAppWithExtension = async () => {
        await setGraphqlQueryIds()
        await storeTabId('storeAppTabId')
    }

    return {
        getExtensionStatus,
        getLoggedInLIUser,
        registerAppWithExtension,
        putSessionTokens,
        openExtensionInTab,
        setUserLoggedAuth,
        getSessionTokens,
        syncConnections,
        syncConversations,
        syncSalesNavigatorConversations,
        getConversationsPage,
        iterateHubSpotQueue,
        getProfileContactInfo,
        getProfileView,
        getProfileExtraData,
        getProfilePosts,
        getJobChanges,
        messageProfile,
        followProfile,
        unfollowProfile,
        disconnectProfile,
        sendConnectionRequest,
        getLDAccessToken,
        setupTemplatesContextMenu,
        storeTabId,
        getLDAuthData,
        getMessagesFromConversation,
        sendMessageInConversation,
        createOrSendMessage,
        getConversationsFromParticipants,
        likeProfilePosts,
        commentProfilePosts,
        setGraphqlQueryIds,
        getJobHistoryData,
        searchPeopleSdui,
        mutualConnectionsFromLinkedinTab,
        getProfileByIdentityDash,
        editProfilePostComment,
        deleteProfilePostComment,
        getProfilePostComments,
        startHubSpotQueueInterval,
        checkHubSpotQueueLastRun,
        queueInboxHubspotExport,
        getHubspotInboxesFromExtension,
        getHubspotOAuthStatusFromExtension,
        inboxMarkAsSeen,
        inboxSyncLinkedinConversationStar,
        inboxArchiveConversation,
        inboxArchiveConversations,
        inboxDeleteConversation,
        visitLinkedInConversation,
        visitLinkedInProfile,
        extractLinkedinProfileHtml,
        resolveLinkedinPostUrns,
        getSalesLists,
        getSalesNavigatorProfile,
        searchLeadsSN,
        ldAddNewConnection,
        getLoggedInLIUserForInbox,
    }
}